import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import CajaFinancieroContabilidadService from "../axios_services/cajafinancierocontabilidad.service";
import CajaDatosService from "../axios_services/cajaflujodatos.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch, ButtonUpdate, ButtonDelete, ButtonInsert, ButtonExcel } from '../components/Buttons/Buttons';
import ModalCajaFinancieroContabilidad from '../components/Modal/ModalCajaFinancieroContabilidad';
import TabsWithTable from '../components/Tabs/TabsWithTable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './CajaFinancieroMovimiento.css';

function CajaFinancieroContabilidad() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [resumen, setResumen] = useState({ categorias: [], total_ingresos: 0, total_egresos: 0, saldo_en_caja: 0 });
    const [tiposComprobante, setTiposComprobante] = useState([]);
    const [categoriasMovimiento, setCategoriasMovimiento] = useState([]);
    const [responsables, setResponsables] = useState([]);
    const [saldoInicial, setSaldoInicial] = useState(0);
    const [saldoInicialInput, setSaldoInicialInput] = useState('0');
    const [puedeEditarSaldoInicial, setPuedeEditarSaldoInicial] = useState(false);
    const [saldoInicialAutomatico, setSaldoInicialAutomatico] = useState(false);
    const [editandoSaldoInicial, setEditandoSaldoInicial] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [registroEditar, setRegistroEditar] = useState(null);
    const [tabActiva, setTabActiva] = useState('movimientos');
    const [toast, setToast] = useState(null);
    const initialFetchDone = useRef(false);
    const toastTimeoutRef = useRef(null);

    const [periodoFecha, setPeriodoFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const formatoSoles = (monto) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(monto || 0);
    };

    const normalizarFecha = (fecha) => {
        if (!fecha) return null;

        if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
            return {
                year: fecha.getFullYear(),
                month: fecha.getMonth() + 1,
                day: fecha.getDate()
            };
        }

        const fechaTexto = fecha.toString().trim();
        const fechaPrincipal = fechaTexto.split('T')[0].split(' ')[0];
        let year;
        let month;
        let day;

        if (fechaPrincipal.includes('-')) {
            [year, month, day] = fechaPrincipal.split('-').map(Number);
        } else if (fechaPrincipal.includes('/')) {
            const partes = fechaPrincipal.split('/').map(Number);
            if (fechaPrincipal.split('/')[0]?.length === 4) {
                [year, month, day] = partes;
            } else {
                [day, month, year] = partes;
            }
        } else {
            const fechaParseada = new Date(fechaTexto);
            if (!Number.isNaN(fechaParseada.getTime())) {
                return {
                    year: fechaParseada.getFullYear(),
                    month: fechaParseada.getMonth() + 1,
                    day: fechaParseada.getDate()
                };
            }
        }

        if (!year || !month || !day) return null;
        return { year, month, day };
    };

    const formatoFecha = (fecha) => {
        const fechaNormalizada = normalizarFecha(fecha);
        if (!fechaNormalizada) return '';

        const { year, month, day } = fechaNormalizada;
        return `${day}/${String(month).padStart(2, '0')}/${year}`;
    };

    const getClaseMonto = (monto) => {
        const valor = parseFloat(monto);
        if (valor < 0) return 'monto-negativo';
        if (valor > 0) return 'monto-positivo';
        return 'monto-neutro';
    };

    const mostrarAcciones = () => {
        return [8, 12, 13, 21, 26].includes(Number(state.user?.perfil_id));
    };

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadInitialData();
            initialFetchDone.current = true;
        }

        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    const showToast = (message, type = 'success') => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }

        setToast({ message, type });
        toastTimeoutRef.current = setTimeout(() => {
            setToast(null);
        }, 3000);
    };

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const perfilId = state.user?.perfil_id;
            const [tiposRes, categoriasRes, responsablesRes] = await Promise.all([
                CajaDatosService.getCajaTipoComprobante(perfilId),
                CajaDatosService.getCajaCategoriaMovimiento(perfilId),
                CajaDatosService.getCajaResponsable(perfilId)
            ]);

            setTiposComprobante(tiposRes.success ? (tiposRes.data || []) : []);
            setCategoriasMovimiento(categoriasRes.success ? (categoriasRes.data || []) : []);
            setResponsables(responsablesRes.success ? (responsablesRes.data || []) : []);

            await fetchContabilidad();
        } catch (error) {
            console.error('Error al cargar datos de contabilidad:', error);
            showToast(error.response?.data?.message || 'Error al cargar contabilidad', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchContabilidad = async () => {
        const response = await CajaFinancieroContabilidadService.getCajaFinancieroContabilidad(
            state.user?.perfil_id,
            periodoFecha
        );

        if (response.success) {
            setRegistros(response.data || []);
            setResumen(response.resumen || { categorias: [], total_ingresos: 0, total_egresos: 0, saldo_en_caja: 0 });
            setSaldoInicial(response.saldo_inicial || 0);
            setSaldoInicialInput((response.saldo_inicial || 0).toString());
            setPuedeEditarSaldoInicial(!!response.puede_editar_saldo_inicial);
            setSaldoInicialAutomatico(!!response.saldo_inicial_automatico);
            setEditandoSaldoInicial(false);
        } else {
            setRegistros([]);
        }
    };

    const handleBuscar = async () => {
        setIsLoading(true);
        try {
            await fetchContabilidad();
        } catch (error) {
            console.error('Error al obtener contabilidad:', error);
            showToast(error.response?.data?.message || 'Error al obtener contabilidad', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGuardarSaldoInicial = async () => {
        if (!puedeEditarSaldoInicial) return;

        if (isNaN(saldoInicialInput)) {
            showToast('Ingrese un saldo inicial válido', 'error');
            return;
        }

        setIsLoading(true);
        try {
            await CajaFinancieroContabilidadService.updateCajaContabilidadSaldoInicial({
                periodo_fecha: periodoFecha,
                saldo_inicial: parseFloat(saldoInicialInput),
                perfil_id: state.user?.perfil_id
            });
            await fetchContabilidad();
            setEditandoSaldoInicial(false);
            showToast('Saldo inicial actualizado');
        } catch (error) {
            console.error('Error al actualizar saldo inicial:', error);
            showToast(error.response?.data?.message || 'Error al actualizar saldo inicial', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleNuevoRegistro = () => {
        setRegistroEditar(null);
        setIsModalOpen(true);
    };

    const handleEditarRegistro = (registro) => {
        setRegistroEditar(registro);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setRegistroEditar(null);
    };

    const handleSaveRegistro = async (registroData) => {
        const dataConPerfil = {
            ...registroData,
            perfil_id: state.user?.perfil_id
        };

        if (registroEditar) {
            await CajaFinancieroContabilidadService.updateCajaFinancieroContabilidad({
                ...dataConPerfil,
                caja_financiero_contabilidad_id: registroEditar.caja_financiero_contabilidad_id
            });
            showToast('Registro contable actualizado');
        } else {
            await CajaFinancieroContabilidadService.insertCajaFinancieroContabilidad(dataConPerfil);
            showToast('Registro contable agregado');
        }

        await fetchContabilidad();
    };

    const handleEliminarRegistro = async (registroId) => {
        if (!window.confirm('¿Está seguro que desea eliminar este registro contable?')) {
            return;
        }

        setIsLoading(true);
        try {
            await CajaFinancieroContabilidadService.deleteCajaFinancieroContabilidad({
                caja_financiero_contabilidad_id: registroId,
                perfil_id: state.user?.perfil_id
            });
            await fetchContabilidad();
            showToast('Registro contable eliminado');
        } catch (error) {
            console.error('Error al eliminar registro contable:', error);
            showToast(error.response?.data?.message || 'Error al eliminar el registro contable', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const resumenConCategorias = () => {
        const resumenMap = new Map((resumen.categorias || []).map(item => [String(item.categoria_id), item]));
        return [...categoriasMovimiento]
            .sort((a, b) => {
                if (a.tipo === 'ingreso' && b.tipo !== 'ingreso') return -1;
                if (a.tipo !== 'ingreso' && b.tipo === 'ingreso') return 1;
                return a.caja_categoria_movimiento_id - b.caja_categoria_movimiento_id;
            })
            .map((categoria) => {
                const match = resumenMap.get(String(categoria.caja_categoria_movimiento_id));
                return {
                    categoria_id: categoria.caja_categoria_movimiento_id,
                    nombre_categoria: categoria.nombre_categoria_movimiento,
                    tipo: categoria.tipo,
                    monto: match?.monto || 0
                };
            });
    };

    const exportarAExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Contabilidad');
        const categorias = resumenConCategorias();
        const egresos = categorias.filter((fila) => fila.tipo !== 'ingreso');
        const ingresos = categorias.filter((fila) => fila.tipo === 'ingreso');
        const todasCategorias = [...egresos, ...ingresos];
        const thinBorder = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        const mediumBorder = {
            top: { style: 'medium', color: { argb: 'FF000000' } },
            left: { style: 'medium', color: { argb: 'FF000000' } },
            bottom: { style: 'medium', color: { argb: 'FF000000' } },
            right: { style: 'medium', color: { argb: 'FF000000' } }
        };
        const moneyFormat = '#,##0.00;[Red]-#,##0.00';
        const setTableCell = (cell, fillColor = null) => {
            cell.border = thinBorder;
            cell.font = { name: 'Arial', size: 8 };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
            if (fillColor) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
            }
        };
        const setHeaderCell = (cell, fillColor = 'FFFFFFFF') => {
            setTableCell(cell, fillColor);
            cell.font = { name: 'Arial', size: 8, bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        };

        const applyOuterBorder = (ws, rStart, rEnd, cStart, cEnd) => {
            for (let r = rStart; r <= rEnd; r++) {
                for (let c = cStart; c <= cEnd; c++) {
                    const cell = ws.getCell(r, c);
                    let currentBorder = cell.border || thinBorder;
                    let newBorder = {
                        top: currentBorder.top || { style: 'thin', color: { argb: 'FF000000' } },
                        left: currentBorder.left || { style: 'thin', color: { argb: 'FF000000' } },
                        bottom: currentBorder.bottom || { style: 'thin', color: { argb: 'FF000000' } },
                        right: currentBorder.right || { style: 'thin', color: { argb: 'FF000000' } }
                    };
                    if (r === rStart) newBorder.top = { style: 'medium', color: { argb: 'FF000000' } };
                    if (r === rEnd) newBorder.bottom = { style: 'medium', color: { argb: 'FF000000' } };
                    if (c === cStart) newBorder.left = { style: 'medium', color: { argb: 'FF000000' } };
                    if (c === cEnd) newBorder.right = { style: 'medium', color: { argb: 'FF000000' } };
                    cell.border = newBorder;
                }
            }
        };

        worksheet.properties.defaultRowHeight = 14;

        worksheet.columns = [
            { width: 3 }, { width: 5 }, { width: 42 }, { width: 16 }, { width: 5 }, { width: 2 }, { width: 2 }, { width: 2 },
            { width: 13 }, { width: 14 }, { width: 5 }, { width: 21 }, { width: 9 }, { width: 11 }, { width: 14 },
            { width: 28 }, { width: 46 }, { width: 5 }, { width: 26 }, { width: 12 }, { width: 5 }, { width: 21 }, { width: 13 }
        ].map((config) => config);

        const detailStartRow = 3;
        const detailEndRow = Math.max(detailStartRow, detailStartRow + registros.length - 1);
        const summaryStartRow = 5;
        const summaryEndRow = summaryStartRow + todasCategorias.length - 1;
        const totalIngresosRow = summaryEndRow + 1;
        const totalEgresosRow = summaryEndRow + 2;
        const saldoCajaRow = summaryEndRow + 3;
        const tipoStartRow = saldoCajaRow + 5;
        const tipoEndRow = tipoStartRow + Math.max(tiposComprobante.length, 1) - 1;
        const responsableStartRow = tipoEndRow + 5;
        const responsableEndRow = responsableStartRow + Math.max(responsables.length, 1) - 1;
        const categoryLookupRange = `$B$${summaryStartRow}:$C$${summaryEndRow}`;
        const tipoLookupRange = `$B$${tipoStartRow}:$C$${tipoEndRow}`;
        const responsableLookupRange = `$B$${responsableStartRow}:$C$${responsableEndRow}`;
        const detailCategoryRange = `$R$${detailStartRow}:$R$${detailEndRow}`;
        const detailAmountRange = `$T$${detailStartRow}:$T$${detailEndRow}`;

        worksheet.getCell('W1').value = parseFloat(saldoInicial) || 0;
        worksheet.getCell('W1').numFmt = moneyFormat;
        worksheet.getCell('W1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
        worksheet.getCell('W1').font = { name: 'Arial', size: 8, bold: true };
        worksheet.getCell('W1').alignment = { vertical: 'middle', horizontal: 'right' };
        worksheet.getCell('W1').border = mediumBorder;

        // Headers para tabla de Categorías
        const summaryHeaderRow = worksheet.getRow(summaryStartRow - 1);
        summaryHeaderRow.getCell(2).value = 'X';
        summaryHeaderRow.getCell(3).value = 'Y';
        summaryHeaderRow.getCell(4).value = 'MONTO';
        [2, 3, 4].forEach(col => setHeaderCell(summaryHeaderRow.getCell(col)));

        todasCategorias.forEach((fila, index) => {
            const rowNumber = summaryStartRow + index;
            const row = worksheet.getRow(rowNumber);
            row.height = 14;
            row.getCell(2).value = fila.categoria_id ? parseInt(fila.categoria_id, 10) : '';
            row.getCell(3).value = fila.nombre_categoria;
            row.getCell(4).value = {
                formula: fila.tipo === 'ingreso'
                    ? `SUMIF(${detailCategoryRange},B${rowNumber},${detailAmountRange})`
                    : `ABS(SUMIF(${detailCategoryRange},B${rowNumber},${detailAmountRange}))`,
                result: parseFloat(fila.monto) || 0
            };
            row.getCell(4).numFmt = moneyFormat;

            [2, 3, 4].forEach((col) => setTableCell(row.getCell(col)));
            row.getCell(2).alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
            row.getCell(4).alignment = { vertical: 'middle', horizontal: 'right', wrapText: false };
        });

        if (egresos.length > 0) {
            worksheet.mergeCells(summaryStartRow, 5, summaryStartRow + egresos.length - 1, 5);
            const cell = worksheet.getCell(summaryStartRow, 5);
            cell.value = 'NEGATIVO';
            cell.font = { name: 'Arial', size: 8, bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center', textRotation: 90 };
            applyOuterBorder(worksheet, summaryStartRow, summaryStartRow + egresos.length - 1, 5, 5);
        }

        if (ingresos.length > 0) {
            const ingresoLabelStart = summaryStartRow + egresos.length;
            worksheet.mergeCells(ingresoLabelStart, 5, ingresoLabelStart + ingresos.length - 1, 5);
            const cell = worksheet.getCell(ingresoLabelStart, 5);
            cell.value = 'POSITIVO';
            cell.font = { name: 'Arial', size: 8, bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center', textRotation: 90 };
            applyOuterBorder(worksheet, ingresoLabelStart, ingresoLabelStart + ingresos.length - 1, 5, 5);
        }

        const ingresoFormulaStart = summaryStartRow + egresos.length;
        worksheet.getCell(`C${totalIngresosRow}`).value = 'TOTAL INGRESOS';
        worksheet.getCell(`D${totalIngresosRow}`).value = {
            formula: ingresos.length > 0 ? `SUM(D${ingresoFormulaStart}:D${summaryEndRow})` : '0',
            result: parseFloat(resumen.total_ingresos) || 0
        };
        worksheet.getCell(`C${totalEgresosRow}`).value = 'TOTAL EGRESOS';
        worksheet.getCell(`D${totalEgresosRow}`).value = {
            formula: egresos.length > 0 ? `SUM(D${summaryStartRow}:D${summaryStartRow + egresos.length - 1})` : '0',
            result: parseFloat(resumen.total_egresos) || 0
        };
        worksheet.getCell(`C${saldoCajaRow}`).value = 'SALDO EN CAJA';
        worksheet.getCell(`D${saldoCajaRow}`).value = {
            formula: `D${totalIngresosRow}+D${totalEgresosRow}`,
            result: parseFloat(resumen.saldo_en_caja) || 0
        };

        [totalIngresosRow, totalEgresosRow, saldoCajaRow].forEach((rowNumber) => {
            ['B', 'C', 'D'].forEach((col) => {
                const cell = worksheet.getCell(`${col}${rowNumber}`);
                setTableCell(cell, rowNumber === totalEgresosRow ? 'FFF4B183' : rowNumber === saldoCajaRow ? 'FFFFFFFF' : 'FF1F1F1F');
                cell.font = { name: 'Arial', size: 8, bold: true, color: { argb: rowNumber === totalIngresosRow ? 'FFFFFFFF' : 'FF000000' } };
            });
            worksheet.getCell(`D${rowNumber}`).numFmt = moneyFormat;
            worksheet.getCell(`D${rowNumber}`).alignment = { vertical: 'middle', horizontal: 'right', wrapText: false };
        });

        // Headers para tabla de Tipo de Comprobante
        const tipoHeaderRow = worksheet.getRow(tipoStartRow - 1);
        tipoHeaderRow.getCell(2).value = '';
        tipoHeaderRow.getCell(3).value = 'TIPO DE COMPROBANTE';
        [2, 3].forEach(col => setHeaderCell(tipoHeaderRow.getCell(col)));

        tiposComprobante.forEach((tipo, index) => {
            const rowNumber = tipoStartRow + index;
            worksheet.getCell(`B${rowNumber}`).value = tipo.caja_tipo_comprobante_id ? parseInt(tipo.caja_tipo_comprobante_id, 10) : '';
            worksheet.getCell(`C${rowNumber}`).value = tipo.nombre_tipo_comprobante;
            setTableCell(worksheet.getCell(`B${rowNumber}`));
            setTableCell(worksheet.getCell(`C${rowNumber}`));
        });

        // Headers para tabla de Responsable
        const responsableHeaderRow = worksheet.getRow(responsableStartRow - 1);
        responsableHeaderRow.getCell(2).value = '';
        responsableHeaderRow.getCell(3).value = 'RESPONSABLE';
        [2, 3].forEach(col => setHeaderCell(responsableHeaderRow.getCell(col)));

        responsables.forEach((responsable, index) => {
            const rowNumber = responsableStartRow + index;
            worksheet.getCell(`B${rowNumber}`).value = responsable.caja_responsable_id ? parseInt(responsable.caja_responsable_id, 10) : '';
            worksheet.getCell(`C${rowNumber}`).value = responsable.nombre_responsable;
            setTableCell(worksheet.getCell(`B${rowNumber}`));
            setTableCell(worksheet.getCell(`C${rowNumber}`));
        });

        const detailHeaders = [
            'FECHA DE MOVIMIENTO',
            'FECHA COMPROBANTE',
            '',
            'TIPO DE COMPROBANTE',
            'SERIE',
            'NUMERO',
            'RUC PROVEEDOR',
            'NOMBRE PROVEEDOR',
            'DESCRIPCION DEL GASTO Y/O INGRESO',
            'X',
            'Y',
            'MONTO',
            '',
            'RESPONSABLE DEL GASTO',
            'SALDO INICIAL'
        ];

        detailHeaders.forEach((header, index) => {
            const col = 9 + index;
            const fillColor = [11, 18, 21].includes(col) ? 'FFFFF2CC' : [12, 19, 22].includes(col) ? 'FFF4B183' : 'FFFFFFFF';
            const cell = worksheet.getCell(2, col);
            cell.value = header;
            setHeaderCell(cell, fillColor);
        });

        registros.forEach((registro, index) => {
            const rowNumber = detailStartRow + index;
            const row = worksheet.getRow(rowNumber);
            const responsableId = registro.caja_responsable_id || '';
            row.height = 14;
            row.getCell(9).value = formatoFecha(registro.fecha_movimiento);
            row.getCell(10).value = formatoFecha(registro.fecha_comprobante);
            row.getCell(11).value = registro.caja_tipo_comprobante_id ? parseInt(registro.caja_tipo_comprobante_id, 10) : '';
            row.getCell(12).value = {
                formula: `IFERROR(VLOOKUP(K${rowNumber},${tipoLookupRange},2,FALSE),"N/D")`,
                result: registro.nombre_tipo_comprobante || ''
            };
            row.getCell(13).value = registro.serie || '';
            row.getCell(14).value = registro.numero || '';
            row.getCell(15).value = registro.ruc_proveedor || '';
            row.getCell(16).value = registro.nombre_proveedor?.toUpperCase() || '';
            row.getCell(17).value = registro.descripcion || '';
            row.getCell(18).value = registro.caja_categoria_movimiento_id ? parseInt(registro.caja_categoria_movimiento_id, 10) : '';
            row.getCell(19).value = {
                formula: `IFERROR(VLOOKUP(R${rowNumber},${categoryLookupRange},2,FALSE),"N/D")`,
                result: registro.nombre_categoria_movimiento || ''
            };
            row.getCell(20).value = parseFloat(registro.monto) || 0;
            row.getCell(21).value = responsableId ? parseInt(responsableId, 10) : '';
            row.getCell(22).value = {
                formula: `IF(U${rowNumber}="","N/D",IFERROR(VLOOKUP(U${rowNumber},${responsableLookupRange},2,FALSE),"N/D"))`,
                result: registro.nombre_responsable || 'N/D'
            };
            row.getCell(23).value = {
                formula: index === 0 ? `$W$1+T${rowNumber}` : `W${rowNumber - 1}+T${rowNumber}`,
                result: parseFloat(registro.saldo) || 0
            };

            for (let col = 9; col <= 23; col += 1) {
                const fillColor = [11, 18, 21].includes(col) ? 'FFFFF2CC' : [12, 19, 22].includes(col) ? 'FFF4B183' : null;
                setTableCell(row.getCell(col), fillColor);
            }

            [9, 10].forEach((col) => {
                row.getCell(col).numFmt = '@';
                row.getCell(col).alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
            });

            [20, 23].forEach((col) => {
                row.getCell(col).numFmt = moneyFormat;
                row.getCell(col).alignment = { vertical: 'middle', horizontal: 'right', wrapText: false };
                if ((parseFloat(registro.monto) || 0) < 0 && col === 20) {
                    row.getCell(col).font = { name: 'Arial', size: 8, color: { argb: 'FFFF0000' } };
                }
            });
        });

        worksheet.getRow(2).height = 30;

        for (let rowNumber = 1; rowNumber <= Math.max(responsableEndRow, detailEndRow); rowNumber += 1) {
            if (rowNumber !== 2) {
                worksheet.getRow(rowNumber).height = 14;
            }
        }

        worksheet.eachRow((row) => {
            row.eachCell((cell) => {
                if (!cell.border) cell.border = thinBorder;
            });
        });

        applyOuterBorder(worksheet, summaryStartRow - 1, summaryStartRow - 1, 2, 4); // Headers Categoria
        applyOuterBorder(worksheet, summaryStartRow, saldoCajaRow, 2, 4); // Data Categoria

        applyOuterBorder(worksheet, tipoStartRow - 1, tipoStartRow - 1, 2, 3); // Headers Tipo
        applyOuterBorder(worksheet, tipoStartRow, tipoEndRow, 2, 3); // Data Tipo

        applyOuterBorder(worksheet, responsableStartRow - 1, responsableStartRow - 1, 2, 3); // Headers Responsable
        applyOuterBorder(worksheet, responsableStartRow, responsableEndRow, 2, 3); // Data Responsable

        applyOuterBorder(worksheet, 2, 2, 9, 23); // Bold border solely around headers
        applyOuterBorder(worksheet, 3, detailEndRow, 9, 23); // Bold border around data


        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        saveAs(blob, `Contabilidad_${periodoFecha}.xlsx`);
    };

    const renderMovimientosTable = () => (
        <div className="table-container contabilidad-table-container">
            <Table>
                <thead>
                    <tr>
                        <th>FECHA DE MOVIMIENTO</th>
                        <th>FECHA COMPROBANTE</th>
                        <th>TIPO DE COMPROBANTE</th>
                        <th>SERIE</th>
                        <th>NUMERO</th>
                        <th>RUC PROVEEDOR</th>
                        <th>NOMBRE PROVEEDOR</th>
                        <th>DESCRIPCION DEL GASTO Y/O INGRESO</th>
                        <th>X</th>
                        <th>Y</th>
                        <th>MONTO</th>
                        <th>RESPONSABLE DEL GASTO</th>
                        <th>SALDO INICIAL</th>
                        {mostrarAcciones() && <th>ACCIONES</th>}
                    </tr>
                </thead>
                <tbody>
                    {registros.length > 0 ? (
                        registros.map((registro) => (
                            <tr key={registro.caja_financiero_contabilidad_id}>
                                <td>{formatoFecha(registro.fecha_movimiento)}</td>
                                <td>{formatoFecha(registro.fecha_comprobante)}</td>
                                <td>{registro.nombre_tipo_comprobante}</td>
                                <td>{registro.serie || ''}</td>
                                <td>{registro.numero || ''}</td>
                                <td>{registro.ruc_proveedor || ''}</td>
                                <td>{registro.nombre_proveedor?.toUpperCase() || ''}</td>
                                <td>{registro.descripcion}</td>
                                <td>{registro.caja_categoria_movimiento_id}</td>
                                <td>{registro.nombre_categoria_movimiento}</td>
                                <td className={`col-monto ${getClaseMonto(registro.monto)}`}>{formatoSoles(registro.monto)}</td>
                                <td>{registro.nombre_responsable || ''}</td>
                                <td>{formatoSoles(registro.saldo)}</td>
                                {mostrarAcciones() && (
                                    <td>
                                        <div className="acciones-container">
                                            <ButtonUpdate onClick={() => handleEditarRegistro(registro)} />
                                            <ButtonDelete onClick={() => handleEliminarRegistro(registro.caja_financiero_contabilidad_id)} />
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={mostrarAcciones() ? 14 : 13} className="text-center text-gray-500 py-4">
                                No hay registros contables disponibles.
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );

    const renderResumenTable = () => (
        <div className="table-container contabilidad-table-container">
            <Table>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>CATEGORÍA</th>
                        <th>TIPO</th>
                        <th>MONTO</th>
                    </tr>
                </thead>
                <tbody>
                    {resumenConCategorias().map((fila) => (
                        <tr
                            key={fila.categoria_id}
                            className={fila.tipo === 'ingreso' ? 'resumen-fila-ingreso' : 'resumen-fila-egreso'}
                        >
                            <td>{fila.categoria_id}</td>
                            <td>{fila.nombre_categoria}</td>
                            <td>{fila.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}</td>
                            <td className="col-monto">{formatoSoles(fila.monto)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );

    const tabs = [
        {
            key: 'movimientos',
            label: 'Movimientos Contables',
            content: renderMovimientosTable()
        },
        {
            key: 'resumen',
            label: 'Resumen por Categoría',
            content: renderResumenTable()
        }
    ];

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="caja-flujo-container contabilidad-scope">
            <div className="flex items-center justify-between mb-4">
                <h1>CONTABILIDAD</h1>
                <ButtonExcel onClick={exportarAExcel} />
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="cont_periodo_fecha">Periodo:</label>
                    <input
                        id="cont_periodo_fecha"
                        type="month"
                        value={periodoFecha}
                        onChange={(e) => setPeriodoFecha(e.target.value)}
                    />
                </div>
            </div>

            <ButtonSearch onClick={handleBuscar} isLoading={isLoading} />

            <div className="numero-propuestas contabilidad-saldos">
                {editandoSaldoInicial ? (
                    <div className="contabilidad-saldo-editor">
                        <label htmlFor="saldo_inicial">Saldo inicial:</label>
                        <input
                            id="saldo_inicial"
                            type="number"
                            step="0.01"
                            value={saldoInicialInput}
                            onChange={(e) => setSaldoInicialInput(e.target.value)}
                        />
                    </div>
                ) : (
                    <p>Saldo inicial: <span>{formatoSoles(saldoInicial)}</span></p>
                )}

                {saldoInicialAutomatico && (
                    <p>Saldo anterior: <span>{formatoSoles(saldoInicial)}</span></p>
                )}

                {!saldoInicialAutomatico && (
                    <p>Saldo anterior: <span>S/ 0</span></p>
                )}

                {puedeEditarSaldoInicial && mostrarAcciones() && (
                    <div className="contabilidad-saldo-actions">
                        {editandoSaldoInicial ? (
                            <>
                                <button className="contabilidad-guardar-saldo" onClick={handleGuardarSaldoInicial}>
                                    Guardar Saldo
                                </button>
                                <button
                                    className="contabilidad-cancelar-saldo"
                                    onClick={() => {
                                        setSaldoInicialInput((saldoInicial || 0).toString());
                                        setEditandoSaldoInicial(false);
                                    }}
                                >
                                    Cancelar
                                </button>
                            </>
                        ) : (
                            <button className="contabilidad-editar-saldo" onClick={() => setEditandoSaldoInicial(true)}>
                                Cambiar Saldo
                            </button>
                        )}
                    </div>
                )}
            </div>

            <TabsWithTable
                tabs={tabs}
                defaultTab="movimientos"
                actionButton={tabActiva === 'movimientos' && mostrarAcciones() ? <ButtonInsert onClick={handleNuevoRegistro} /> : null}
                onTabChange={setTabActiva}
            />

            <ModalCajaFinancieroContabilidad
                onSave={handleSaveRegistro}
                onClose={handleCloseModal}
                isOpen={isModalOpen}
                registroEditar={registroEditar}
                periodo={periodoFecha}
                tiposComprobante={tiposComprobante}
                categoriasMovimiento={categoriasMovimiento}
                responsables={responsables}
                onNotify={showToast}
            />

            {toast && (
                <div className={`contabilidad-toast ${toast.type === 'error' ? 'error' : 'success'}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
}

export default CajaFinancieroContabilidad;
